import * as THREE from 'three';

import { Animation, AnimationConfig } from './types';

// Constants that replace GUI controls
const ANIMATION_PARAMS = {
  // Controls the Perlin noise-based animation
  perlin: {
    time: 2.0, // Animation speed - higher values = faster movement
    morph: 1, // Base sphere surface distortion amount
    dnoise: 2, // Detail noise intensity - affects surface texture
  },
  // Voice reactivity settings
  voice: {
    morphPerturb: {
      min: 0.0, // Minimum morph value when voice is active
      max: 10, // Maximum morph value when voice is active
      smooth: 0.85, // Smoothing factor for morph perturbation (0-1)
    },
    dnoisePerturb: {
      min: 0, // Minimum dnoise value when voice is active
      max: 5, // Maximum dnoise value when voice is active
      smooth: 0.85, // Smoothing factor for dnoise perturbation (0-1)
    },
  },
  // Controls the color mixing and noise patterns
  chroma: {
    colorIntensity: {
      primary: 1, // Intensity of primary color (lavender)
      secondary: 1, // Intensity of secondary color (peach)
      highlight: 2, // Intensity of highlight color (beige)
    },
    RGBn: 0.1, // Black noise intensity
    RGBm: 2, // Color mixing/chroma intensity
  },
  // Controls the camera position and movement
  camera: {
    zoom: 150, // Camera distance from sphere (lower = closer)
    speedY: 0.06, // Rotation speed around Y axis
    speedX: 0.0, // Rotation speed around X axis
  },
  // Controls the sphere's appearance
  sphere: {
    wireframe: false, // Show wireframe instead of solid surface
    points: true, // Show points instead of solid surface
    psize: 0.5, // Size of points when points mode is enabled
    pointSizeOverride: false, // If true, psize overrides props value
  },
  // Controls all colors in the scene
  colors: {
    // Hemisphere light colors for ambient lighting
    ambient: {
      sky: '#878787', // Color of light shining from above
      ground: '#878787', // Color of light shining from below
    },
    // Point light colors for directional lighting
    lights: {
      back: '#E5A59A', // Color of light source behind the sphere
      front: '#d9b2b2', // Color of light source in front of the sphere
    },
    // Shader material colors for sphere surface
    material: {
      primary: '#d9b2b2', // Base color for the sphere surface (lavender)
      secondary: '#E5A59A', // Secondary color for blending effects (peach)
      highlight: '#E5A59A', // Color used for highlights and emphasis (beige)
    },
  },
};

export class ZaraAnimation3D implements Animation {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private mesh: THREE.Mesh;
  private point: THREE.Points;
  private frame: number = Date.now();
  private uniforms: { [key: string]: THREE.IUniform };
  private lastVoiceActivity: number = 0;
  private currentMorph: number = ANIMATION_PARAMS.perlin.morph;
  private currentDnoise: number = ANIMATION_PARAMS.perlin.dnoise;
  private targetMorph: number = ANIMATION_PARAMS.perlin.morph;
  private targetDnoise: number = ANIMATION_PARAMS.perlin.dnoise;
  private readonly RETURN_TO_REST_SPEED = 0.05; // Speed of return to resting state
  private pointSize: number;

  constructor({ canvas, size, pointSize = 0.5 }: AnimationConfig) {
    // Store pointSize for later use
    this.pointSize = pointSize;

    // Scene setup
    this.scene = new THREE.Scene();
    this.scene.fog = null;

    // Camera setup
    this.camera = new THREE.PerspectiveCamera(20, 1, 1, 1000);
    this.camera.position.set(0, 10, 120);

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(size, size);
    this.renderer.setClearColor(0x000000, 0);

    // Lights setup
    const ambientLight = new THREE.HemisphereLight(
      ANIMATION_PARAMS.colors.ambient.sky,
      ANIMATION_PARAMS.colors.ambient.ground,
      1,
    );
    const backLight = new THREE.PointLight(
      ANIMATION_PARAMS.colors.lights.back,
      1,
    );
    backLight.position.set(-5, -20, -20);
    const frontLight = new THREE.PointLight(
      ANIMATION_PARAMS.colors.lights.front,
      2,
    );
    frontLight.position.set(20, 10, 0);

    this.scene.add(ambientLight);
    this.scene.add(backLight);
    this.scene.add(frontLight);

    // Uniforms setup
    this.uniforms = {
      time: { value: 0.0 },
      RGBr: { value: 0.0 },
      RGBg: { value: 0.0 },
      RGBb: { value: 0.0 },
      RGBn: { value: 0.0 },
      RGBm: { value: 0.0 },
      morph: { value: 0.0 },
      dnoise: { value: 0.0 },
      // Use point size based on override setting
      psize: {
        value: ANIMATION_PARAMS.sphere.pointSizeOverride
          ? ANIMATION_PARAMS.sphere.psize
          : pointSize,
      },
      primaryColor: {
        value: new THREE.Color(ANIMATION_PARAMS.colors.material.primary),
      },
      secondaryColor: {
        value: new THREE.Color(ANIMATION_PARAMS.colors.material.secondary),
      },
      highlightColor: {
        value: new THREE.Color(ANIMATION_PARAMS.colors.material.highlight),
      },
    };

    // Create geometry with higher detail level
    const geometry = new THREE.IcosahedronGeometry(20, 15);

    // Create material with shaders
    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: this.getNoiseVertexShader(),
      fragmentShader: this.getFragmentShader(),
      side: THREE.DoubleSide,
      wireframe: ANIMATION_PARAMS.sphere.wireframe,
    });

    // Create mesh and points
    this.mesh = new THREE.Mesh(geometry, material);
    this.point = new THREE.Points(geometry, material);

    this.scene.add(this.mesh);
    this.scene.add(this.point);

    // Initial visibility
    this.mesh.visible = !ANIMATION_PARAMS.sphere.points;
    this.point.visible = ANIMATION_PARAMS.sphere.points;
  }

  private getNoiseVertexShader(): string {
    return `
      vec3 mod289(vec3 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
      }
      vec4 mod289(vec4 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
      }
      vec4 permute(vec4 x) {
        return mod289(((x*34.0)+1.0)*x);
      }
      vec4 taylorInvSqrt(vec4 r) {
        return 1.79284291400159 - 0.85373472095314 * r;
      }
      vec3 fade(vec3 t) {
        return t*t*t*(t*(t*6.0-15.0)+10.0);
      }
      float pnoise(vec3 P, vec3 rep) {
        vec3 Pi0 = mod(floor(P), rep);
        vec3 Pi1 = mod(Pi0 + vec3(1.0), rep);
        Pi0 = mod289(Pi0);
        Pi1 = mod289(Pi1);
        vec3 Pf0 = fract(P);
        vec3 Pf1 = Pf0 - vec3(1.0);
        vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
        vec4 iy = vec4(Pi0.yy, Pi1.yy);
        vec4 iz0 = Pi0.zzzz;
        vec4 iz1 = Pi1.zzzz;
        vec4 ixy = permute(permute(ix) + iy);
        vec4 ixy0 = permute(ixy + iz0);
        vec4 ixy1 = permute(ixy + iz1);
        vec4 gx0 = ixy0 * (1.0 / 7.0);
        vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
        gx0 = fract(gx0);
        vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
        vec4 sz0 = step(gz0, vec4(0.0));
        gx0 -= sz0 * (step(0.0, gx0) - 0.5);
        gy0 -= sz0 * (step(0.0, gy0) - 0.5);
        vec4 gx1 = ixy1 * (1.0 / 7.0);
        vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
        gx1 = fract(gx1);
        vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
        vec4 sz1 = step(gz1, vec4(0.0));
        gx1 -= sz1 * (step(0.0, gx1) - 0.5);
        gy1 -= sz1 * (step(0.0, gy1) - 0.5);
        vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
        vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
        vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
        vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
        vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
        vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
        vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
        vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);
        vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
        g000 *= norm0.x;
        g010 *= norm0.y;
        g100 *= norm0.z;
        g110 *= norm0.w;
        vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
        g001 *= norm1.x;
        g011 *= norm1.y;
        g101 *= norm1.z;
        g111 *= norm1.w;
        float n000 = dot(g000, Pf0);
        float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
        float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
        float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
        float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
        float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
        float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
        float n111 = dot(g111, Pf1);
        vec3 fade_xyz = fade(Pf0);
        vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
        vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
        float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
        return 2.0 * n_xyz;
      }
      varying vec3 vNormal;
      uniform float time;
      uniform float morph;
      uniform float psize;
      
      void main() {
        float f = morph * pnoise(normal + time, vec3(10.0));
        vNormal = normalize(normal);
        vec4 pos = vec4(position + f * normal, 1.0);
        gl_Position = projectionMatrix * modelViewMatrix * pos;
        gl_PointSize = psize;
      }
    `;
  }

  private getFragmentShader(): string {
    return `
      varying vec3 vNormal;
      uniform float time;
      uniform float RGBr;
      uniform float RGBg;
      uniform float RGBb;
      uniform float RGBn;
      uniform float RGBm;
      uniform float dnoise;
      uniform vec3 primaryColor;
      uniform vec3 secondaryColor;
      uniform vec3 highlightColor;
      
      vec3 mod289(vec3 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
      }
      vec4 mod289(vec4 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
      }
      vec4 permute(vec4 x) {
        return mod289(((x*34.0)+1.0)*x);
      }
      vec4 taylorInvSqrt(vec4 r) {
        return 1.79284291400159 - 0.85373472095314 * r;
      }
      vec3 fade(vec3 t) {
        return t*t*t*(t*(t*6.0-15.0)+10.0);
      }
      float pnoise(vec3 P, vec3 rep) {
        vec3 Pi0 = mod(floor(P), rep);
        vec3 Pi1 = mod(Pi0 + vec3(1.0), rep);
        Pi0 = mod289(Pi0);
        Pi1 = mod289(Pi1);
        vec3 Pf0 = fract(P);
        vec3 Pf1 = Pf0 - vec3(1.0);
        vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
        vec4 iy = vec4(Pi0.yy, Pi1.yy);
        vec4 iz0 = Pi0.zzzz;
        vec4 iz1 = Pi1.zzzz;
        vec4 ixy = permute(permute(ix) + iy);
        vec4 ixy0 = permute(ixy + iz0);
        vec4 ixy1 = permute(ixy + iz1);
        vec4 gx0 = ixy0 * (1.0 / 7.0);
        vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
        gx0 = fract(gx0);
        vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
        vec4 sz0 = step(gz0, vec4(0.0));
        gx0 -= sz0 * (step(0.0, gx0) - 0.5);
        gy0 -= sz0 * (step(0.0, gy0) - 0.5);
        vec4 gx1 = ixy1 * (1.0 / 7.0);
        vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
        gx1 = fract(gx1);
        vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
        vec4 sz1 = step(gz1, vec4(0.0));
        gx1 -= sz1 * (step(0.0, gx1) - 0.5);
        gy1 -= sz1 * (step(0.0, gy1) - 0.5);
        vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
        vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
        vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
        vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
        vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
        vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
        vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
        vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);
        vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
        g000 *= norm0.x;
        g010 *= norm0.y;
        g100 *= norm0.z;
        g110 *= norm0.w;
        vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
        g001 *= norm1.x;
        g011 *= norm1.y;
        g101 *= norm1.z;
        g111 *= norm1.w;
        float n000 = dot(g000, Pf0);
        float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
        float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
        float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
        float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
        float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
        float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
        float n111 = dot(g111, Pf1);
        vec3 fade_xyz = fade(Pf0);
        vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
        vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
        float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
        return 10.0 * n_xyz;
      }
      
      void main() {
        float r = pnoise(RGBr * (vNormal + time), vec3(10.0));
        float g = pnoise(RGBg * (vNormal + time), vec3(10.0));
        float b = pnoise(RGBb * (vNormal + time), vec3(10.0));
        float n = pnoise(-1.0 * (vNormal + time), vec3(10.0));
        n = 50.0 * pnoise((RGBn) * vNormal, vec3(10.0)) * pnoise(RGBm * (vNormal + time), vec3(10.0));
        n -= 0.10 * pnoise(dnoise * vNormal, vec3(10.0));
        
        // Mix colors based on noise
        vec3 noiseColor = vec3(r + n, g + n, b + n);
        vec3 baseColor = mix(primaryColor, secondaryColor, noiseColor.r);
        vec3 finalColor = mix(baseColor, highlightColor, noiseColor.g * 0.5);
        
        gl_FragColor = vec4(finalColor, 1.0);
      }
    `;
  }

  private getVoiceActivity(dataArray: Uint8Array): number {
    const sampleRate = 44100;
    const fftSize = 2048;
    const frequencyBinSize = sampleRate / fftSize;
    const startBin = Math.floor(85 / frequencyBinSize);
    const endBin = Math.ceil(255 / frequencyBinSize);

    let voiceEnergy = 0;
    for (let i = startBin; i <= endBin; i++) {
      voiceEnergy += dataArray[i];
    }

    // Clamp and smooth the voice activity
    const rawActivity = voiceEnergy / (endBin - startBin + 1) / 255;
    return Math.min(Math.max(rawActivity, 0), 1); // Ensure value is between 0 and 1
  }

  private lerpToTarget(current: number, target: number, speed: number): number {
    const diff = target - current;
    if (Math.abs(diff) < 0.001) return target;
    return current + diff * speed;
  }

  update(audioData?: Uint8Array, isAiSpeaking?: boolean): void {
    const time = Date.now();

    // Process voice activity
    if (audioData) {
      let voiceActivity = this.getVoiceActivity(audioData);

      // Apply different sensitivity for AI vs human voice
      const sensitivity = isAiSpeaking ? 1.2 : 1.0; // Slightly higher sensitivity for AI voice
      voiceActivity *= sensitivity;

      // Smooth the voice activity transition with different smoothing for AI/human
      const smoothFactor = isAiSpeaking
        ? ANIMATION_PARAMS.voice.morphPerturb.smooth * 0.9 // Slightly faster response for AI
        : ANIMATION_PARAMS.voice.morphPerturb.smooth;

      this.lastVoiceActivity =
        this.lastVoiceActivity * smoothFactor +
        voiceActivity * (1 - smoothFactor);

      // Calculate target values based on voice activity
      const morphRange =
        ANIMATION_PARAMS.voice.morphPerturb.max -
        ANIMATION_PARAMS.voice.morphPerturb.min;
      this.targetMorph =
        ANIMATION_PARAMS.voice.morphPerturb.min +
        morphRange * this.lastVoiceActivity;

      const dnoiseRange =
        ANIMATION_PARAMS.voice.dnoisePerturb.max -
        ANIMATION_PARAMS.voice.dnoisePerturb.min;
      this.targetDnoise =
        ANIMATION_PARAMS.voice.dnoisePerturb.min +
        dnoiseRange * this.lastVoiceActivity;
    } else {
      // When silent, gradually return to resting state
      this.targetMorph = ANIMATION_PARAMS.perlin.morph;
      this.targetDnoise = ANIMATION_PARAMS.perlin.dnoise;
      this.lastVoiceActivity *= ANIMATION_PARAMS.voice.morphPerturb.smooth;
    }

    // Smoothly interpolate current values towards target values
    this.currentMorph = this.lerpToTarget(
      this.currentMorph,
      this.targetMorph,
      this.RETURN_TO_REST_SPEED,
    );

    this.currentDnoise = this.lerpToTarget(
      this.currentDnoise,
      this.targetDnoise,
      this.RETURN_TO_REST_SPEED,
    );

    // Update uniforms with smoothed values
    this.uniforms.time.value =
      (ANIMATION_PARAMS.perlin.time / 10000) * (time - this.frame);
    this.uniforms.morph.value = this.currentMorph;
    this.uniforms.dnoise.value = this.currentDnoise;

    // Rest of the update function remains the same
    this.camera.position.z = 300 - ANIMATION_PARAMS.camera.zoom;

    this.mesh.rotation.y += ANIMATION_PARAMS.camera.speedY / 100;
    this.mesh.rotation.z += ANIMATION_PARAMS.camera.speedX / 100;
    this.point.rotation.y = this.mesh.rotation.y;
    this.point.rotation.z = this.mesh.rotation.z;

    this.uniforms.RGBr.value =
      ANIMATION_PARAMS.chroma.colorIntensity.primary / 10;
    this.uniforms.RGBg.value =
      ANIMATION_PARAMS.chroma.colorIntensity.secondary / 10;
    this.uniforms.RGBb.value =
      ANIMATION_PARAMS.chroma.colorIntensity.highlight / 10;
    this.uniforms.RGBn.value = ANIMATION_PARAMS.chroma.RGBn / 100;
    this.uniforms.RGBm.value = ANIMATION_PARAMS.chroma.RGBm;
    // Update psize using point size based on override setting
    this.uniforms.psize.value = ANIMATION_PARAMS.sphere.pointSizeOverride
      ? ANIMATION_PARAMS.sphere.psize
      : this.pointSize;

    this.mesh.visible = !ANIMATION_PARAMS.sphere.points;
    this.point.visible = ANIMATION_PARAMS.sphere.points;

    (this.mesh.material as THREE.ShaderMaterial).wireframe =
      ANIMATION_PARAMS.sphere.wireframe;

    this.camera.lookAt(this.scene.position);
    this.renderer.render(this.scene, this.camera);
  }

  cleanup(): void {
    // Clean up Three.js resources
    this.renderer.dispose();
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.point.geometry.dispose();
    (this.point.material as THREE.Material).dispose();
    this.scene.clear();
  }
}
